/* Bezahlte Rechnungen prüfen (21.10.2026, backend/einzug/bezahlt.py): Solange Sticky noch selbst einzieht, stehen
   festgeschriebene Rechnungen hier als offen, obwohl sie abgebucht sind. Die Prüfung legt sie neben die hochgeladenen
   Sticky-Dateien und den Zahlstatus in Lexware. Erst „als bezahlt vermerken“ schreibt etwas: Die Rechnungen kommen
   dann in keinen CRM-Einzug mehr. */
import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Upload, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const euro = (v) => (v ?? 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const tag = (iso) => iso ? new Date(`${iso}T12:00`).toLocaleDateString('de-DE') : '';
const QUELLE = {
  sticky: ['von Sticky eingezogen', 'text-emerald-700 dark:text-emerald-400'],
  lexware: ['in Lexware bezahlt', 'text-emerald-700 dark:text-emerald-400'],
  ruecklastschrift: ['nach Rücklastschrift offen', 'text-amber-700 dark:text-amber-400'],
  offen: ['noch offen', 'text-foreground'],
};

export default function BezahltPruefen({ monat, onVermerkt }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState('');
  const [abwahl, setAbwahl] = useState({});
  const [frage, setFrage] = useState(false);
  const [zeigen, setZeigen] = useState(false);

  const pruefen = async () => {
    setBusy('pruefen');
    try { setD((await api.get('/einzug/bezahlt', { params: { monat } })).data); setAbwahl({}); setFrage(false); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Prüfung fehlgeschlagen'); }
    setBusy('');
  };
  const hochladen = async (e) => {
    const dateien = Array.from(e.target.files || []); e.target.value = '';
    if (!dateien.length) return;
    setBusy('upload');
    try {
      const fd = new FormData(); dateien.forEach(f => fd.append('dateien', f));
      const r = (await api.post('/lexware/abgleich/sepa', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
      toast.success(`${r.dateien} Dateien: ${r.neu} Lastschriften neu, ${r.schon_da} schon bekannt`);
      if (r.fehler?.length) toast.error(r.fehler.join(' | '), { duration: 10000 });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload fehlgeschlagen'); }
    setBusy('');
    pruefen();
  };

  const bezahlt = (d?.rechnungen || []).filter(z => z.quelle === 'sticky' || z.quelle === 'lexware');
  const gewaehlt = bezahlt.filter(z => !abwahl[z.rv_id]);
  const vermerken = async () => {
    setFrage(false); setBusy('vermerken');
    try {
      const r = (await api.post('/einzug/bezahlt/vermerken', { rv_ids: gewaehlt.map(z => z.rv_id) })).data;
      toast.success(`${r.vermerkt} Rechnungen als bezahlt vermerkt${r.abgelehnt ? ` · ${r.abgelehnt} nicht (nicht mehr bezahlt erkannt)` : ''}`);
      onVermerkt?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Vermerken fehlgeschlagen'); }
    setBusy('');
    pruefen();
  };

  const z = d?.zusammen;
  return (
    <div className="rounded-lg border border-border/60 p-3 text-sm space-y-2" data-testid="bezahlt-pruefen">
      <div className="flex flex-wrap items-center gap-2">
        <b>Bezahlte Rechnungen prüfen</b>
        <span className="text-xs text-muted-foreground flex-1 min-w-[16rem]">Vor jedem Einzug: Was Sticky schon abgebucht hat oder in Lexware bezahlt ist, darf nicht noch einmal eingezogen werden.</span>
        <label className={`inline-flex items-center gap-1 text-xs underline cursor-pointer ${busy ? 'pointer-events-none opacity-50' : ''}`}>
          <Upload className="w-3 h-3" />Sticky-Datei hochladen
          <input type="file" accept=".xml" multiple className="hidden" onChange={hochladen} data-testid="bezahlt-upload" />
        </label>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={pruefen} data-testid="bezahlt-pruefen-knopf">
          {busy === 'pruefen' || busy === 'upload' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Jetzt prüfen'}
        </Button>
      </div>

      {d && <>
        <p className="text-xs text-muted-foreground">
          Geprüft: {d.rechnungen.length} festgeschriebene Rechnungen bis {monat} ohne Einzug.
          {d.sticky_dateien.lastschriften ? ` Sticky-Dateien: ${d.sticky_dateien.lastschriften} Lastschriften, letzter Einzugstag ${tag(d.sticky_dateien.letzter_einzugstag)}.` : ' Noch keine Sticky-Datei hochgeladen.'}
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-1">
          {Object.entries(QUELLE).map(([q, [lab, cls]]) => (
            <span key={q} className={cls}><b className="tabular-nums">{z[q].anzahl}</b> {lab} <span className="text-muted-foreground tabular-nums">({euro(z[q].summe)})</span></span>
          ))}
        </div>
        {bezahlt.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" className="bg-marke hover:bg-marke-dunkel" disabled={!!busy || !gewaehlt.length} onClick={() => setFrage(true)} data-testid="bezahlt-vermerken">
              {gewaehlt.length} als bezahlt vermerken ({euro(gewaehlt.reduce((s, x) => s + x.betrag, 0))})
            </Button>
            <button className="text-xs underline" onClick={() => setZeigen(x => !x)}>{zeigen ? 'Liste ausblenden' : 'Liste ansehen und abwählen'}</button>
          </div>
        )}
        {frage && (
          <div className="rounded-md border border-marke/40 bg-marke/5 p-3 space-y-2" data-testid="bezahlt-frage">
            <p>{gewaehlt.length} Rechnungen ({euro(gewaehlt.reduce((s, x) => s + x.betrag, 0))}) gelten danach als eingezogen und kommen in <b>keinen</b> CRM-Einzug mehr. Kommt eine als Rücklastschrift zurück, gibt der Bank-Abgleich sie wieder frei.</p>
            <div className="flex gap-2">
              <Button size="sm" className="bg-marke hover:bg-marke-dunkel" onClick={vermerken} data-testid="bezahlt-bestaetigen">Ja, vermerken</Button>
              <Button size="sm" variant="outline" onClick={() => setFrage(false)}>Abbrechen</Button>
            </div>
          </div>
        )}
        {zeigen && (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground"><tr>
                <th className="w-6"></th><th className="text-left py-1 font-semibold">Zahler</th><th className="text-left py-1 font-semibold">Rechnung</th>
                <th className="text-left py-1 font-semibold">Stand</th><th className="text-right py-1 font-semibold">Betrag</th>
              </tr></thead>
              <tbody>{d.rechnungen.map(x => {
                const [lab, cls] = QUELLE[x.quelle];
                const waehlbar = x.quelle === 'sticky' || x.quelle === 'lexware';
                return (
                  <tr key={x.rv_id} className="border-t border-border/40 align-top">
                    <td className="py-1">{waehlbar && <input type="checkbox" className="accent-marke" checked={!abwahl[x.rv_id]} onChange={e => setAbwahl(a => ({ ...a, [x.rv_id]: !e.target.checked }))} />}</td>
                    <td className="py-1">{x.zahler_name}</td>
                    <td className="py-1">{x.rechnungsnummer}<span className="text-muted-foreground"> · {x.monat}</span></td>
                    <td className={`py-1 ${cls}`}>
                      <span className="inline-flex items-center gap-1">{waehlbar && <CheckCircle2 className="w-3 h-3" />}{lab}{x.einzugstag ? ` am ${tag(x.einzugstag)}` : ''}</span>
                      {x.hinweise.map((h, i) => <div key={i} className="text-amber-700 dark:text-amber-400 flex gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 flex-none" />{h}{h.startsWith('Betrag') ? ` (Sticky ${euro(x.betrag_sticky)})` : ''}</div>)}
                    </td>
                    <td className="py-1 text-right tabular-nums">{euro(x.betrag)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </>}
    </div>
  );
}
